import {
  Archive,
  File,
  FileCode,
  FileImage,
  FileSpreadsheet,
  FileText,
  FileType,
  Music,
  Presentation,
  Video,
} from 'lucide-react';

import { useTranslation } from '@/hooks/useTranslation';

interface FileTypeMapping {
  extensions: readonly string[];
  mimeKeywords: readonly string[]; // Added to handle keyword matching
  icon: React.ComponentType<React.SVGProps<SVGSVGElement>>;
}

// File type mappings - extracted to module level for performance
const FILE_TYPE_MAPPINGS: Record<string, FileTypeMapping> = {
  pdf: {
    extensions: ['pdf'],
    mimeKeywords: ['application/pdf'],
    icon: FileText,
  },
  images: {
    extensions: ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp', 'svg'],
    mimeKeywords: ['image/'],
    icon: FileImage,
  },
  videos: {
    extensions: ['mp4', 'avi', 'mov', 'wmv', 'flv', 'webm', 'mkv'],
    mimeKeywords: ['video/'],
    icon: Video,
  },
  audio: {
    extensions: ['mp3', 'wav', 'ogg', 'flac', 'aac', 'm4a'],
    mimeKeywords: ['audio/'],
    icon: Music,
  },
  spreadsheets: {
    extensions: ['xls', 'xlsx', 'csv', 'ods'],
    mimeKeywords: ['spreadsheet', 'excel'],
    icon: FileSpreadsheet,
  },
  presentations: {
    extensions: ['ppt', 'pptx', 'odp'],
    mimeKeywords: ['presentation'],
    icon: Presentation,
  },
  documents: {
    extensions: ['doc', 'docx', 'txt', 'rtf', 'odt'],
    mimeKeywords: ['document', 'word', 'text'],
    icon: FileText,
  },
  code: {
    extensions: [
      'js',
      'ts',
      'jsx',
      'tsx',
      'html',
      'css',
      'json',
      'xml',
      'php',
      'py',
      'java',
      'cpp',
      'c',
      'cs',
      'rb',
      'go',
      'rs',
      'swift',
      'kt',
    ],
    mimeKeywords: ['application/javascript', 'text/html', 'text/css'],
    icon: FileCode,
  },
  archives: {
    extensions: ['zip', 'rar', '7z', 'tar', 'gz', 'bz2'],
    mimeKeywords: ['application/zip', 'application/x-rar'],
    icon: Archive,
  },
  misc: {
    extensions: ['log', 'ini', 'cfg', 'conf'],
    mimeKeywords: ['text/plain'],
    icon: FileType,
  },
};

/**
 * Determines the appropriate file type icon based on MIME type and filename
 *
 * This function prioritizes MIME type matching over extension matching for accuracy.
 * It supports keyword matching in MIME types for more flexible categorization.
 *
 * @param mimeType - The MIME type of the file (e.g., "application/pdf", "image/jpeg")
 * @param fileName - The name of the file including extension (e.g., "document.pdf")
 * @returns The appropriate Lucide React icon component for the file type
 *
 * @example
 * ```tsx
 * const PdfIcon = getFileTypeIcon("application/pdf", "document.pdf");
 * const ImageIcon = getFileTypeIcon("image/jpeg", "photo.jpg");
 * ```
 */
export const getFileTypeIcon = (
  mimeType: string,
  fileName: string
): React.ComponentType<React.SVGProps<SVGSVGElement>> => {
  const extension = fileName.split('.').pop() || '';
  const normalizedExtension = extension.toLowerCase();
  const normalizedMimeType = mimeType.toLowerCase();

  // First, try to match by MIME type keywords (more accurate)
  for (const mapping of Object.values(FILE_TYPE_MAPPINGS)) {
    for (const keyword of mapping.mimeKeywords) {
      if (normalizedMimeType.includes(keyword.toLowerCase())) {
        return mapping.icon;
      }
    }
  }

  // Fallback to extension matching
  for (const mapping of Object.values(FILE_TYPE_MAPPINGS)) {
    if (mapping.extensions.includes(normalizedExtension)) {
      return mapping.icon;
    }
  }

  // Default fallback
  return File;
};

/**
 * Formats file size in bytes to a human-readable string with appropriate units
 *
 * This function converts file sizes from bytes to appropriate units (B, KB, MB, GB)
 * with localized unit labels. It preserves 2 decimal places for precision.
 *
 * @param bytes - The file size in bytes
 * @param t - Translation function from useTranslation hook
 * @returns Formatted file size string with appropriate unit
 *
 * @example
 * ```tsx
 * const { t } = useTranslation();
 * formatFileSize(1024, t); // "1.00 KB"
 * formatFileSize(0, t); // "0 Bytes"
 * formatFileSize(1536000, t); // "1.46 MB"
 * ```
 */
export const formatFileSize = (
  bytes: number,
  t: ReturnType<typeof useTranslation>['t']
): string => {
  if (bytes === 0) return `0 ${t('common.units.fileSize.bytes')}`;

  const k = 1024;
  const sizes = [
    t('common.units.fileSize.bytes'),
    t('common.units.fileSize.kb'),
    t('common.units.fileSize.mb'),
    t('common.units.fileSize.gb'),
  ];

  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
};
